/**
 * FeedbackPanel — Side panel / bottom sheet for the feedback widget.
 *
 * Responsive layout:
 *  - Desktop: 360px right-side panel
 *  - Mobile (<768px): bottom sheet (60vh)
 *
 * Contains: SessionHeader, RecordButton, RecordingIndicator,
 * FeedbackHelpPanel (first-time instructions), CommentList, SessionActions,
 * TextCommentInput (text-based comment alternative).
 *
 * When proposals are present, shows IssueProposalReview instead of
 * CommentList for the user to review/edit/confirm aggregated issues.
 */
import { SessionHeader } from "./SessionHeader";
import { cn } from "../../utils/cn";
import { RecordButton } from "./RecordButton";
import { RecordingIndicator } from "./RecordingIndicator";
import { CommentList } from "./CommentList";
import { SessionActions } from "./SessionActions";
import { IssueProposalReview } from "./IssueProposalReview";
import { FeedbackHelpPanel } from "./FeedbackHelpPanel";
import { TextCommentInput } from "./TextCommentInput";
import type { FeedbackSession, IssueProposal } from "../../types/feedback";
import { useState } from "react";

interface FeedbackPanelProps {
  readonly session: FeedbackSession;
  readonly isRecording: boolean;
  readonly recordingDuration: number;
  readonly interimTranscript: string;
  readonly isSaving: boolean;
  readonly isAggregating: boolean;
  readonly proposals: IssueProposal[] | null;
  readonly helpDismissedPersisted?: boolean;
  readonly onClose: () => void;
  readonly onToggleRecording: () => void;
  readonly onSave: () => void;
  readonly onEndSession: () => void;
  readonly onDeleteComment: (index: number) => void;
  readonly onRenameSession: (name: string) => void;
  readonly onUpdateProposal: (index: number, proposal: IssueProposal) => void;
  readonly onDeleteProposal: (index: number) => void;
  readonly onConfirmProposals: () => void;
  readonly onCancelProposals: () => void;
  readonly onAddTextComment?: (text: string) => void;
  readonly onHelpDismissedPersisted?: (persist: boolean) => void;
}

export function FeedbackPanel({
  session,
  isRecording,
  recordingDuration,
  interimTranscript,
  isSaving,
  isAggregating,
  proposals,
  helpDismissedPersisted = false,
  onClose,
  onToggleRecording,
  onSave,
  onEndSession,
  onDeleteComment,
  onRenameSession,
  onUpdateProposal,
  onDeleteProposal,
  onConfirmProposals,
  onCancelProposals,
  onAddTextComment,
  onHelpDismissedPersisted,
}: FeedbackPanelProps) {
  const showProposalReview = proposals !== null;
  const [isExpanded, setIsExpanded] = useState(false);
  const [helpDismissed, setHelpDismissed] = useState(helpDismissedPersisted);
  const [helpManuallyShown, setHelpManuallyShown] = useState(false);

  const showHelp =
    helpManuallyShown ||
    (!showProposalReview &&
      session.comments.length === 0 &&
      !isRecording &&
      !helpDismissed);

  const handleHelpDismiss = (dontShowAgain: boolean) => {
    setHelpDismissed(true);
    setHelpManuallyShown(false);
    if (dontShowAgain) {
      onHelpDismissedPersisted?.(true);
    }
  };

  return (
    <div
      className={cn("fb-panel", isExpanded && "fb-panel--expanded")}
      role="complementary"
      aria-label="Feedback panel"
    >
      <button
        type="button"
        className="fb-panel-expand"
        onClick={() => setIsExpanded((expanded) => !expanded)}
        aria-label={
          isExpanded ? "Shrink feedback panel" : "Expand feedback panel"
        }
        title={isExpanded ? "Shrink panel" : "Expand panel"}
      >
        {isExpanded ? "⇥" : "⇤"}
      </button>

      {/* Close button */}
      <button
        type="button"
        className="fb-panel-close"
        onClick={onClose}
        aria-label="Close feedback panel"
      >
        ✕
      </button>

      {/* Help button — visible when help is not currently shown */}
      {!showHelp && !showProposalReview && (
        <button
          type="button"
          className="fb-panel-help-btn"
          onClick={() => setHelpManuallyShown(true)}
          aria-label="Show help"
          title="Show instructions"
        >
          ?
        </button>
      )}

      {/* Session header */}
      <SessionHeader
        sessionName={session.sessionName}
        commentCount={session.comments.length}
        onNameChange={onRenameSession}
      />

      {/* Recording controls — hidden during proposal review and help */}
      {!showProposalReview && !showHelp && (
        <div className="fb-recording-controls">
          <RecordButton
            isRecording={isRecording}
            disabled={isSaving || isAggregating}
            onToggle={onToggleRecording}
          />
          {isRecording && (
            <RecordingIndicator
              durationSeconds={recordingDuration}
              interimTranscript={interimTranscript}
            />
          )}
        </div>
      )}

      {/* Body: Help, Comments, or Proposal Review */}
      <div className="fb-panel-body">
        {showHelp ? (
          <FeedbackHelpPanel onDismiss={handleHelpDismiss} />
        ) : showProposalReview ? (
          <IssueProposalReview
            proposals={proposals}
            onUpdateProposal={onUpdateProposal}
            onDeleteProposal={onDeleteProposal}
            onConfirm={onConfirmProposals}
            onCancel={onCancelProposals}
          />
        ) : (
          <CommentList comments={session.comments} onDelete={onDeleteComment} />
        )}
      </div>

      {/* Text comment input — shown when not in help or proposal mode */}
      {!showProposalReview && !showHelp && onAddTextComment && (
        <TextCommentInput
          onSubmit={onAddTextComment}
          disabled={isSaving || isAggregating}
        />
      )}

      {/* Actions — hidden during proposal review and help (review has its own buttons) */}
      {!showProposalReview && !showHelp && (
        <SessionActions
          commentCount={session.comments.length}
          isSaving={isSaving}
          isAggregating={isAggregating}
          onSave={onSave}
          onEndSession={onEndSession}
        />
      )}
    </div>
  );
}
